import type { ListBuilderList } from '../../../types';
import {
	createClientActionsColumn,
	createClientNameColumn,
	createClientRecencyDateColumn,
	createClientSalesRoutesColumn,
	createLocationColumn,
	createSalespeopleColumn,
} from '../columns';
import {
	CLIENT_MOBILE_CARD_CONFIG,
	DEFAULT_LIST_REQUIRED_FIELDS,
	SALESPEOPLE_FILTER_RULES,
} from '../shared';

/**
 * PD-15924 (decision 4) — Sales Routing list for clients.
 *
 * The sales-side twin of Delivery Routing: which live sales routes cover each
 * client, when a rep last visited, when the next planned stop is and when the
 * last delivery landed — so "who is on a route, and who have we not seen?"
 * is one list, not a route-by-route hunt.
 *
 * Bundled for every tenant but INACTIVE by default, exactly like the delivery
 * list, so tenants that do not run sales routes never see it.
 */
export const clientSalesRoutingList: ListBuilderList = {
	id: 'client-sales-routing',
	cacheTTL: 600000,
	name: 'Sales Routing',
	type: 'default',
	active: false,
	bulkActionTemplate: 'clients',
	sortOrder: 16,
	columns: [
		createClientNameColumn(),
		createClientSalesRoutesColumn(),
		createClientRecencyDateColumn('lastVisitDate', 'Last Visit'),
		createClientRecencyDateColumn('lastDeliveryDate', 'Last Delivery'),
		createLocationColumn(),
		createSalespeopleColumn(),
		createClientActionsColumn(),
	],
	mobileCardConfig: CLIENT_MOBILE_CARD_CONFIG,
	description: 'Customers, the sales routes that cover them and visit recency',
	defaultSortId: 'column-client-name-asc',
	// `salesRoutes` is attached after the query and `nextSalesStopDate` is a
	// scalar subquery; ClientsService only does either when asked for.
	requiredFields: [
		...DEFAULT_LIST_REQUIRED_FIELDS,
		'salesRoutes',
		'client.nextSalesStopDate',
		'client.lastVisitDate',
		'client.lastDeliveryDate',
	],
	// Recency first (the question the list exists for), then coverage, then
	// the shared client-list order.
	activeFilters: [
		'clientSearch',
		'lastVisitDays',
		'lastDeliveryDays',
		'clientSalesRouted',
		'salespeople',
		'alternateRep',
		'clientCompany',
		'lifecycleStageName',
		'businessTypes',
		'leadOrCustomer',
		'salesStatus',
		'callStatus',
		'clientState',
		'revenue90Days',
		'orderSalesStatus',
		'groups',
		'clientLists',
		'excludeClientLists',
		'clientDeletedRecords',
		'clientArchivedRecords',
	],
	filterRules: {
		// The server derives a user's client visibility from the requested
		// list's salespeople rule (PD-15546); without it a `view:self` user
		// could read the whole book through this listId.
		...SALESPEOPLE_FILTER_RULES,
		leadOrCustomer: {
			defaultValue: [{ id: 'Customer', label: 'Customer' }],
		},
	},
};
